import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Table, Project, NotificationRule, NotificationProvider, TableColumn } from '../../types/domain';
import { apiClient } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { CreateRuleModal } from '../modals/CreateRuleModal';
import { ConfirmModal } from '../modals/ConfirmModal';
import { HedgehogSpinner } from '../brand/HedgehogSpinner';
import { PageContainer } from '../layout/PageContainer';
import { Button } from '../ui/Button';
import {
  Mail,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Webhook,
  Power,
  Sparkles,
  Edit2,
} from 'lucide-react';

interface RulesTabProps {
  table: Table;
  project: Project;
  canEdit: boolean;
  columns?: TableColumn[];
  headerActionContainer?: HTMLElement | null;
}

export const RulesTab: React.FC<RulesTabProps> = ({
  table,
  project,
  canEdit,
  columns,
  headerActionContainer,
}) => {
  const { showToast } = useToast();

  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [providers, setProviders] = useState<NotificationProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<NotificationRule | null>(null);

  const fetchRulesAndProviders = useCallback(async () => {
    try {
      // 1. Fetch table-scoped rules
      try {
        const rulesRes = await apiClient<{ data: NotificationRule[] }>(
          `/projects/${project.id}/tables/${table.id}/rules`
        );
        setRules(rulesRes.data || []);
      } catch (err) {
        console.warn('Could not fetch table rules from API:', err);
        setRules([]);
      }

      // 2. Fetch project providers
      try {
        const provRes = await apiClient<{ data: NotificationProvider[] }>(
          `/projects/${project.id}/providers`
        );
        setProviders(provRes.data || []);
      } catch (err) {
        console.warn('Could not fetch providers from API:', err);
        setProviders([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [project.id, table.id]);

  useEffect(() => {
    fetchRulesAndProviders();
  }, [fetchRulesAndProviders]);

  const handleToggleRule = async (rule: NotificationRule) => {
    if (!canEdit) return;
    setTogglingRuleId(rule.id);

    try {
      // Optimistic update
      const updatedState = !rule.is_active;
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, is_active: updatedState } : r))
      );

      await apiClient(`/projects/${project.id}/tables/${table.id}/rules/${rule.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          is_active: updatedState,
        }),
      }).catch(() => {});

      showToast({
        type: 'success',
        title: updatedState ? 'Rule Activated' : 'Rule Paused',
        message: `Notification rule '${rule.name}' is now ${updatedState ? 'active' : 'paused'}.`,
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to update rule',
        message: err?.message || 'Server error',
      });
      fetchRulesAndProviders();
    } finally {
      setTogglingRuleId(null);
    }
  };

  const handleDeleteRule = (rule: NotificationRule) => {
    if (!canEdit) return;
    setRuleToDelete(rule);
  };

  const executeDeleteRule = async () => {
    if (!ruleToDelete || !canEdit) return;
    const rule = ruleToDelete;
    setDeletingRuleId(rule.id);
    try {
      await apiClient(`/projects/${project.id}/tables/${table.id}/rules/${rule.id}`, {
        method: 'DELETE',
      });

      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      showToast({
        type: 'success',
        title: 'Rule Deleted',
        message: `Rule '${rule.name}' was removed.`,
      });
      setRuleToDelete(null);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to delete rule',
        message: err?.message || 'Server error',
      });
    } finally {
      setDeletingRuleId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <HedgehogSpinner size="md" label="LOADING NOTIFICATION RULES..." />
      </div>
    );
  }

  return (
    <PageContainer variant="tab">
      {/* Portaled Header Action */}
      {canEdit && headerActionContainer
        ? createPortal(
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                setEditingRule(null);
                setCreateRuleOpen(true);
              }}
              leftIcon={<Plus size={14} />}
            >
              Create Notification Rule
            </Button>,
            headerActionContainer
          )
        : null}

      {/* Rules Table */}
      <div className="bg-cf-surface border border-cf-border rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full min-w-[640px] text-left text-xs border-collapse">
          <thead>
            <tr className="bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader text-cf-textSecondary uppercase text-xs border-b border-cf-border dark:border-[#2e323c]">
              <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Rule Name</th>
              <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Trigger Event</th>
              <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Provider</th>
              <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Recipients</th>
              <th className="py-2.5 px-4 font-semibold text-center bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Status</th>
              <th className="py-2.5 px-4 font-semibold text-right bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cf-border text-xs">
            {rules.map((rule) => {
              const matchedProvider = providers.find((p) => p.id === rule.provider_id);
              return (
                <tr key={rule.id} className="bg-cf-surface hover:bg-cf-surfaceHover transition-colors">
                  <td className="py-3 px-4">
                    <div
                      onClick={() => {
                        if (canEdit) {
                          setEditingRule(rule);
                          setCreateRuleOpen(true);
                        }
                      }}
                      className={`font-semibold text-cf-textPrimary ${
                        canEdit ? 'hover:text-cf-orange cursor-pointer' : ''
                      }`}
                      title={canEdit ? 'Click to edit rule & template' : undefined}
                    >
                      {rule.name}
                    </div>
                    <div className="text-xs text-cf-textMuted truncate max-w-xs">
                      {rule.subject_template}
                    </div>
                  </td>

                  <td className="py-3 px-4 text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textSecondary uppercase text-xs">
                      {rule.event_trigger.replace('_', ' ')}
                    </span>
                  </td>

                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      {matchedProvider?.provider_type === 'smtp' && (
                        <Mail size={13} className="text-cf-orange" />
                      )}
                      {matchedProvider?.provider_type === 'webhook' && (
                        <Webhook size={13} className="text-sky-400" />
                      )}
                      {matchedProvider?.provider_type === 'resend' && (
                        <Send size={13} className="text-emerald-400" />
                      )}
                      <span className="text-cf-textPrimary text-xs">
                        {matchedProvider?.name || 'Default Provider'}
                      </span>
                      <span className="text-xs uppercase px-1 py-0.2 rounded bg-cf-bg border border-cf-border text-cf-textMuted">
                        {matchedProvider?.provider_type || 'smtp'}
                      </span>
                    </div>
                  </td>

                  <td className="py-3 px-4 text-cf-textSecondary text-xs max-w-xs truncate">
                    {rule.recipients.join(', ')}
                  </td>

                  <td className="py-3 px-4 text-center">
                    <Button
                      type="button"
                      variant={rule.is_active ? 'secondary' : 'ghost'}
                      size="xs"
                      onClick={() => handleToggleRule(rule)}
                      disabled={togglingRuleId === rule.id || !canEdit}
                      className={`rounded-full ${
                        rule.is_active
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-medium'
                          : 'text-cf-textMuted'
                      }`}
                      title="Click to toggle active state"
                      leftIcon={<Power size={10} />}
                    >
                      {rule.is_active ? 'Active' : 'Paused'}
                    </Button>
                  </td>

                  <td className="py-3 px-4 text-right">
                    {canEdit && (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => {
                            setEditingRule(rule);
                            setCreateRuleOpen(true);
                          }}
                          title="Edit Rule & Email Template"
                          aria-label="Edit Rule & Email Template"
                        >
                          <Edit2 size={13} />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDeleteRule(rule)}
                          disabled={deletingRuleId === rule.id}
                          title="Delete Rule"
                          aria-label="Delete Rule"
                          className="hover:text-rose-400"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {rules.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-cf-textSecondary italic text-xs">
                  No notification rules configured for this table yet.{' '}
                  {canEdit && (
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      onClick={() => {
                        setEditingRule(null);
                        setCreateRuleOpen(true);
                      }}
                      className="ml-1"
                    >
                      Create your first rule
                    </Button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Interactive Create / Edit Rule Modal */}
      {createRuleOpen && (
        <CreateRuleModal
          isOpen={createRuleOpen}
          onClose={() => {
            setCreateRuleOpen(false);
            setEditingRule(null);
          }}
          onSuccess={(savedRule) => {
            if (editingRule) {
              setRules((prev) => prev.map((r) => (r.id === savedRule.id ? savedRule : r)));
            } else {
              setRules((prev) => [savedRule, ...prev]);
            }
            setCreateRuleOpen(false);
            setEditingRule(null);
          }}
          initialRule={editingRule}
          table={table}
          project={project}
          providers={providers}
          columns={columns}
        />
      )}

      {/* Delete Rule Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(ruleToDelete)}
        onClose={() => setRuleToDelete(null)}
        onConfirm={executeDeleteRule}
        title="Delete Notification Rule"
        message={
          <>
            Are you sure you want to delete <strong className="text-cf-textPrimary font-semibold">{ruleToDelete?.name}</strong>? Automated email dispatches attached to this rule will cease immediately.
          </>
        }
        confirmLabel="Delete Rule"
        isLoading={Boolean(deletingRuleId)}
      />
    </PageContainer>
  );
};
